/**
 * Custom element <awe-footer> for displaying a footer section with optional expandable content.
 *
 * - If the "long" attribute is set (or its value is "true"), the footer will be collapsible/expandable.
 * - Shows a "See more"/"See less" button when content is long.
 * - Emits a "footer-expand" event when toggled, with details about the expanded state and height.
 *
 * @example
 * <awe-footer long="true">Some long content...</awe-footer>
 *
 * @fires footer-expand - Dispatched when the footer is expanded or collapsed.
 */
class AweFooter extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.contentNd = document.createElement('div');
        this.contentNd.classList.add('content');
        this.contentNd.innerHTML = '<slot></slot>';
        const style = document.createElement('style');
        style.textContent = ``;
        this.shadowRoot?.append(style, this.contentNd);
    }
    connectedCallback() {
        if (!this.isConnected)
            return;
        const longAttr = this.getAttribute('long');
        const isLong = longAttr === '' || longAttr === 'true';
        this.contentNd.classList.toggle('long', isLong);
        this.toggleButton(isLong);
    }
    /**
     * Sets or removes the button according to the slotted content length.
     * @param isLong - Whether the content is considered long and requires a toggle button.
     */
    toggleButton(isLong) {
        let btn = this.shadowRoot?.getElementById('btn');
        if (isLong && !btn) {
            btn = document.createElement('button');
            btn.id = 'btn';
            const txt = window.i18n.get('AWE-FOOTER.SEE_MORE');
            btn.textContent = txt;
            btn.setAttribute('aria-label', txt);
            this.shadowRoot?.append(btn);
            btn.addEventListener('click', e => { this.toggleFullText(e); });
        }
        else if (!isLong && btn)
            btn.remove();
    }
    /**
     * Toggles the expanded state of the footer content and emits a "footer-expand" event.
     * @param e - The mouse event that triggered the toggle.
     */
    toggleFullText(e) {
        let height;
        if (!this.contentNd.classList.contains('expanded'))
            height = this.shadowRoot?.host.getBoundingClientRect().height;
        const expanded = this.contentNd.classList.toggle('expanded');
        const event = new CustomEvent('footer-expand', {
            bubbles: true,
            detail: {
                expanded,
                height,
            },
        });
        this.dispatchEvent(event);
        e.currentTarget.textContent = window.i18n.get(expanded ? 'AWE-FOOTER.SEE_LESS' : 'AWE-FOOTER.SEE_MORE');
    }
}
customElements.define('awe-footer', AweFooter);
const footerNd = document.getElementsByTagName('awe-footer')[0];
if (footerNd) {
    // on footer-expand event, set layout margin bottom to exactly the height of the collapsed footer,
    // so that there is no layout shift for the stores when moving the footer out of the grid.
    footerNd.addEventListener('footer-expand', (e) => {
        const customEvent = e;
        e.currentTarget?.classList.toggle('expanded', customEvent.detail.expanded);
        if (customEvent.detail.height)
            document.body.style.setProperty('--layout-margin-bottom', `${customEvent.detail.height.toFixed(1)}px`);
        else
            document.body.style.removeProperty('--layout-margin-bottom');
    });
}
